import {
  addElement,
  defaultGenericElement,
  getNextRetouchLayer,
  reversionElement,
  updateElement,
  type GenericElement,
} from '../../lib/element';
import {
  prepareElementForExport,
  roundExportVal,
} from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { useEditorStore } from '../../stores/useEditorStore';
import { distance, type Point } from '../../utils/math';
import { pointsToShape } from '../../utils/shapes';
import { translateSvgPathWithSubpaths } from '../../utils/svg';
import { type GenericFreedrawElement, freedraw } from '../freedraw';
import { getFreeDrawElementAbsoluteCoords } from '../freedraw/utils/coords';
import { getFreedrawShape } from '../freedraw/utils/shape';
import { general } from '../general';

type Mask = {
  data: string;
  dx: number;
  dy: number;
  width: number;
  height: number;
};

export interface RetouchGradientElement extends GenericFreedrawElement {
  type: 'retouchGradient';
  mask: Mask;
  colors: string[];
  startPoint: Point;
  endPoint: Point;
}

const state: {
  limitToMask: boolean;
  colors: string[];
} = {
  limitToMask: true,
  colors: ['rgba(60, 135, 247, 1)', 'rgba(255, 255, 255, 0.5)'],
};

const actions = {
  activate: () => {
    general.actions.activate(plugin.name, false, false);
  },
  setColors: (colors: string[]) => {
    state.colors = colors;
  },
  setLimitToMask: (limit: boolean) => {
    state.limitToMask = limit;
  },
  updateColorForElement: (id: string, color: string) => {
    const elements = useEditorStore.getState().elements;
    const element = elements.get(id) as RetouchGradientElement;

    updateElement<RetouchGradientElement>(elements, element, {
      strokeColor: color,
      ...reversionElement(element, ['canvas']),
    });

    renderStaticScene();
  },
};

const plugin: Plugin<RetouchGradientElement> & {
  actions: typeof actions;
  state: typeof state;
} = {
  name: 'retouchGradient',
  cursor: 'crosshair',
  actions,
  state: state,
  events: {
    onPointerDown: ({ event, helpers, runtimeState, elementState }) => {
      const { button, pos } = event;

      if (
        button === 'left' &&
        runtimeState.activeTool === 'retouchGradient' &&
        !runtimeState.newElement
      ) {
        const selectedElementId =
          useEditorStore.getState().selectedElementIds[0];
        const selectedElement = useEditorStore
          .getState()
          .elements.get(selectedElementId);

        const image = Array.from(elementState.elements.values()).find(
          (element) => element.type === 'image',
        );

        const getMask = () => {
          if (
            selectedElement != null &&
            selectedElement.shape != null &&
            state.limitToMask
          ) {
            return {
              data:
                selectedElement.type !== 'boolean'
                  ? translateSvgPathWithSubpaths(
                      selectedElement.shape.data,
                      -selectedElement.x,
                      -selectedElement.y,
                    )
                  : selectedElement.shape.data,
              dx: selectedElement.x,
              dy: selectedElement.y,
              width: selectedElement.width,
              height: selectedElement.height,
            };
          }

          if (image != null) {
            const imagePath = pointsToShape(
              [
                [0, 0],
                [image.width, 0],
                [image.width, image.height],
                [0, image.height],
              ],
              0,
              0,
              undefined,
              {
                isClosed: true,
                strokeWidth: 0,
                strokeCap: 'round',
                simplify: false,
              },
            );

            return {
              data: imagePath,
              dx: image.x,
              dy: image.y,
              width: image.width,
              height: image.height,
            };
          }

          return null;
        };

        const mask = getMask();
        if (mask == null) return;

        const newElement: RetouchGradientElement = {
          ...defaultGenericElement(),
          layer: getNextRetouchLayer(),
          originX: pos.x,
          originY: pos.y,
          points: [[0, 0]],
          isOpen: true,
          path: new Path2D(),
          type: 'retouchGradient',
          x: pos.x,
          y: pos.y,
          colors: state.colors,
          mask,
          isLocked: true,
          startPoint: [0, 0],
          endPoint: [0, 0],
          strokeColor: state.colors[0],
          strokeWidth: 3,
          strokeCap: 'square',
        };

        helpers.updateRuntimeStore({
          newElement: newElement as GenericElement,
          originalNewElement: { ...newElement } as GenericElement,
        });
        helpers.showNewElementCanvas();
        helpers.renderNewElementScene();
        return;
      }
    },
    onPointerMove: ({ event, helpers, runtimeState }) => {
      const { pos } = event;

      if (
        runtimeState.newElement &&
        runtimeState.newElement.type === 'retouchGradient'
      ) {
        const element = runtimeState.newElement as RetouchGradientElement;

        const newElement = {
          ...element,
          ...reversionElement(element, ['bounds', 'canvas']),
          points: [
            [element.points[0][0], element.points[0][1]],
            [pos.x - element.x, pos.y - element.y],
          ],
        } as GenericElement;

        const [x1, y1, x2, y2] = getFreeDrawElementAbsoluteCoords(
          newElement as RetouchGradientElement,
        );
        const width = distance(x1, x2);
        const height = distance(y1, y2);

        helpers.updateRuntimeStore({
          newElement: { ...newElement, height, width },
        });
        helpers.renderNewElementScene();
      }
    },
    onPointerUp: ({ elementState, helpers, history, runtimeState }) => {
      const element = runtimeState.newElement as RetouchGradientElement;

      if (
        !element ||
        runtimeState.activeTool !== 'retouchGradient' ||
        element.points.length < 2
      ) {
        return;
      }

      const translatedStartPoint: Point = [
        element.startPoint[0] + element.x - element.mask.dx,
        element.startPoint[1] + element.y - element.mask.dy,
      ];
      const translatedEndPoint: Point = [
        element.points[1][0] + element.x - element.mask.dx,
        element.points[1][1] + element.y - element.mask.dy,
      ];

      const gradientElement: RetouchGradientElement = {
        ...element,
        width: element.mask.width,
        height: element.mask.height,
        x: element.mask.dx,
        y: element.mask.dy,
        startPoint: translatedStartPoint,
        endPoint: translatedEndPoint,
        points: [
          [0, 0],
          [element.mask.width, element.mask.height],
        ],
      };

      gradientElement.version = reversionElement(gradientElement, [
        'canvas',
        'bounds',
      ]).version;

      addElement(elementState.elements, gradientElement);
      history.push(elementState);
      updateRuntimeStore({ newElement: undefined });

      helpers.renderStaticScene();
      helpers.hideNewElementCanvas();
    },
    blockTouchPanning: true,
  },
  render: {
    ...freedraw.render,
    draw: ({ config, context, element, runtimeState }) => {
      if (!runtimeState.newElement && !element.points.length) return;

      if (element.renderedById != null || element.points.length < 2) return;

      context.save();

      const mask = new Path2D(
        translateSvgPathWithSubpaths(
          element.mask.data,
          element.mask.dx - element.x + element.strokeWidth / 2,
          element.mask.dy - element.y + element.strokeWidth / 2,
        ),
      );

      // While second point is not set just draw a line to the mouse
      if (
        element.startPoint[0] === element.endPoint[0] &&
        element.startPoint[1] === element.endPoint[1]
      ) {
        const path = new Path2D();
        path.moveTo(element.points[0][0], element.points[0][1]);
        path.lineTo(element.points[1][0], element.points[1][1]);

        context.strokeStyle = element.strokeColor;
        context.lineWidth = element.strokeWidth;
        context.stroke(path);
        context.restore();
        return;
      }

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      if (mask == null) return;

      const gradient = context.createLinearGradient(
        element.startPoint[0],
        element.startPoint[1],
        element.endPoint[0],
        element.endPoint[1],
      );
      element.colors.forEach((color, index) => {
        gradient.addColorStop(index / (element.colors.length - 1), color);
      });

      context.fillStyle = gradient;
      context.fill(mask);

      context.restore();
    },
    isRetouchTool: true,
  },
  export: (element) => {
    const prepared = prepareElementForExport(
      element,
    ) as Partial<RetouchGradientElement>;

    prepared.points = element.points.map(([x, y]) => [
      roundExportVal(x),
      roundExportVal(y),
    ]);

    return prepared;
  },
  collision: {
    onlyCountDirectElementHitAfterSelection: false,
    bounds: freedraw.collision.bounds,
    calculateShape: () => ({ data: '', type: 'line' }),
    isPointInShape: () => false,
    isPointOnShape: () => false,
  },
  transformation: {
    shiftBySizeOnFlip: false,
    resizedBounds: () => [0, 0, 0, 0, 0, 0],
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as RetouchGradientElement;

    imported.shape = getFreedrawShape(imported);

    return imported;
  },
};

export const retouchGradient = plugin;
